import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { DEFAULT_AGENT_CONFIG } from "@web-src/modules/cx/pages/CXAIAgentsPage/consts";
import { AgentStyleSlider } from "./components/AgentStyleSlider";
import {
  EMOJI_USAGE_VALUES,
  TONE_VALUES,
  getDiscreteIndex,
  getDiscreteIndexByValue,
  getDiscreteValueByIndex,
  getSanitizedStyleConfig,
} from "./utils";

export const AgentStylePanel: React.FC<{ entityId: string }> = ({
  entityId,
}) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { integrationsApi } = useRestApiProvider();

  const [conversationToneLevel, setConversationToneLevel] = useState(0);
  const [emojiUsageLevel, setEmojiUsageLevel] = useState(0);
  const [replyDetails, setReplyDetails] = useState(0);

  const { data, error, isLoading, isFetching } =
    integrationsApi.useGetAiAgentConfigQuery({ entityId });
  const [updateAgentConfig] = integrationsApi.useUpdateAgentConfigMutation();
  const [createAgentConfig] = integrationsApi.useCreateAgentConfigMutation();

  const showErrorToast = useCallback(() => {
    toast(
      t({
        id: "feedback.something-went-wrong",
        defaultMessage: "Something went wrong",
      }),
      { variant: "error" }
    );
  }, [toast, t]);

  useEffect(() => {
    if (!data || isLoading || isFetching) return;

    const sanitizedConfig = getSanitizedStyleConfig(data.config);

    setConversationToneLevel(
      getDiscreteValueByIndex(
        getDiscreteIndexByValue(sanitizedConfig.tone, TONE_VALUES)
      )
    );
    setEmojiUsageLevel(
      getDiscreteValueByIndex(
        getDiscreteIndexByValue(sanitizedConfig.emoji_usage, EMOJI_USAGE_VALUES)
      )
    );
    setReplyDetails(sanitizedConfig.response_length);
  }, [data, isFetching, isLoading]);

  useEffect(() => {
    if (!data || isLoading || isFetching) return;

    const sanitizedConfig = getSanitizedStyleConfig(data.config);
    const shouldUpdateBackend =
      sanitizedConfig.tone !== data.config.tone ||
      sanitizedConfig.emoji_usage !== data.config.emoji_usage ||
      sanitizedConfig.response_length !== data.config.response_length;

    if (!shouldUpdateBackend) return;

    updateAgentConfig({
      entityId,
      config: sanitizedConfig,
    }).catch(showErrorToast);
  }, [
    data,
    entityId,
    isFetching,
    isLoading,
    showErrorToast,
    updateAgentConfig,
  ]);

  useEffect(() => {
    if (data || isLoading) return;

    createAgentConfig({
      entityId,
      config: DEFAULT_AGENT_CONFIG,
    }).catch(() => {
      showErrorToast();
    });
  }, [
    createAgentConfig,
    data,
    entityId,
    error,
    isFetching,
    isLoading,
    showErrorToast,
  ]);

  const persistAgentStyleConfig = useCallback(() => {
    if (!data) return;

    const nextConfig = {
      ...data.config,
      tone: TONE_VALUES[getDiscreteIndex(conversationToneLevel)],
      emoji_usage: EMOJI_USAGE_VALUES[getDiscreteIndex(emojiUsageLevel)],
      response_length: Math.round(replyDetails),
    };

    const isDirty =
      nextConfig.tone !== data.config.tone ||
      nextConfig.emoji_usage !== data.config.emoji_usage ||
      nextConfig.response_length !== data.config.response_length;

    if (!isDirty) return;

    updateAgentConfig({
      entityId,
      config: nextConfig,
    }).catch(showErrorToast);
  }, [
    conversationToneLevel,
    data,
    emojiUsageLevel,
    entityId,
    replyDetails,
    showErrorToast,
    updateAgentConfig,
  ]);

  const controls = useMemo<
    {
      key: "conversationTone" | "emojiUsage" | "replyDetails";
      title: string;
      value: number;
      step: number;
      onChange: (value: number) => void;
      valueLabel?: string;
      scaleLabels?: [string, string, string];
    }[]
  >(() => {
    const toneLabels: [string, string, string] = [
      t({
        id: "cx-ai-agents-page.agent-tone-business",
        defaultMessage: "Business",
      }),
      t({
        id: "cx-ai-agents-page.agent-tone-neutral",
        defaultMessage: "Neutral",
      }),
      t({
        id: "cx-ai-agents-page.agent-tone-creative",
        defaultMessage: "Creative",
      }),
    ];
    const emojiLabels: [string, string, string] = [
      t({
        id: "cx-ai-agents-page.emoji-usage-none",
        defaultMessage: "None",
      }),
      t({
        id: "cx-ai-agents-page.emoji-usage-average",
        defaultMessage: "Average",
      }),
      t({
        id: "cx-ai-agents-page.emoji-usage-many",
        defaultMessage: "Many",
      }),
    ];

    return [
      {
        key: "conversationTone",
        title: t({
          id: "cx-ai-agents-page.conversation-tone",
          defaultMessage: "Conversation tone",
        }),
        value: conversationToneLevel,
        step: 5,
        onChange: setConversationToneLevel,
        scaleLabels: toneLabels,
      },
      {
        key: "emojiUsage",
        title: t({
          id: "cx-ai-agents-page.emoji-usage",
          defaultMessage: "Emojis usage",
        }),
        value: emojiUsageLevel,
        step: 5,
        onChange: setEmojiUsageLevel,
        scaleLabels: emojiLabels,
      },
      {
        key: "replyDetails",
        title: t({
          id: "cx-ai-agents-page.replies-details-level",
          defaultMessage: "How detailed should replies be?",
        }),
        value: replyDetails,
        step: 1,
        valueLabel: String(Math.round(replyDetails)),
        onChange: (value) => setReplyDetails(Math.round(value)),
      },
    ];
  }, [conversationToneLevel, emojiUsageLevel, replyDetails, t]);

  if (!data) return null;
  return (
    <div
      className="flex flex-col gap-10"
      onMouseUpCapture={persistAgentStyleConfig}
    >
      {controls.map((control) => (
        <div key={control.key} className="flex flex-col">
          <div className="flex flex-col gap-2">
            <div className="font-secondary text-dark flex items-center justify-between text-sm leading-[21px]">
              <span>{control.title}</span>
              <span>{control.valueLabel}</span>
            </div>
            <AgentStyleSlider
              min={0}
              max={10}
              step={control.step}
              value={control.value}
              onChange={control.onChange}
              width="100%"
            />
          </div>
          {control.scaleLabels && (
            <div className="font-secondary text-grey-900 leading-2 flex items-center justify-between text-xs">
              <span>{control.scaleLabels[0]}</span>
              <span>{control.scaleLabels[1]}</span>
              <span>{control.scaleLabels[2]}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
